import React, { useState } from 'react';
import { connect } from 'react-redux';
import { ColumnApi, GridApi } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import MailToCellRenderer from '$/components/global/mailToCellRenderer';
import NoDataFound from '$/components/global/noData';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import {
  getIsCurrentUserClientState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import { getIsCurrentUserAdminState } from '$/store/profile/selectors';
import Modal from '$/components/global/modal';
import DataSitesLegend from '$/components/global/dataSitesLegend/index';
import './index.scss';

interface IMnpiAccessProps {
  dataSites: Bixby.Models.ClientDataSiteForClient[] | undefined;
  isAdmin: boolean;
  isClient: boolean;
  isCreditSightsOpsSufOrRatings: boolean;
}

const MnpiDataSitesAccess: React.FC<IMnpiAccessProps> = props => {
  const { isAdmin, isClient, isCreditSightsOpsSufOrRatings, dataSites } = props;

  const [, setGridApi] = useState(null as GridApi | null);
  const [, setGridColumnApi] = useState(null as ColumnApi | null);

  const [modalActive, setModalActive] = useState(false);

  const handleOpenModal = () => {
    setModalActive(true);
  };

  const handleCloseModal = () => {
    setModalActive(false);
  };

  let clientDataSitesViewHeader: string | null = null;
  if (dataSites && !Bixby.Utils.isNullOrEmpty(dataSites)) {
    const clientDataSiteUploadDate: Date = dataSites[0].uploadDate;
    const uploadDate: string = new Date(clientDataSiteUploadDate).toLocaleDateString();
    clientDataSitesViewHeader = `Data Sites: ${dataSites.length} (last updated ${uploadDate})`;
  }

  const onGridReady = (params: any) => {
    setGridApi(params.api);
    setGridColumnApi(params.columnApi);
  };

  const getContextMenuItems = () => {
    const result = ['copy', 'copyWithHeaders', 'separator', 'export'];
    return result;
  };

  const content = isAdmin ? (
    <React.Fragment>
      <section>
        <div className="data-sites-access-section">
          <h3>Admin User</h3>
        </div>
      </section>
    </React.Fragment>
  ) : isCreditSightsOpsSufOrRatings ? (
    <React.Fragment>
      <section>
        <div className="data-sites-access-section">
          <h3>CreditSights User</h3>
        </div>
      </section>
    </React.Fragment>
  ) : isClient ? (
    <React.Fragment>
      <section>
        <div className="data-sites-access-note">
          <p>{clientDataSitesViewHeader}</p>
        </div>
        <div className="data-sites-access-header">
          <p className="data-sites-access-header-note">
            Note:{' '}
            <a className="legend-link" onClick={handleOpenModal}>
              See Legend
            </a>{' '}
            for status details; data site access and Bixby status updated daily; will not reflect intraday changes.
          </p>
          <p className="data-sites-access-header-note">
            *Not currently covered by Bixby; please reach out for further information.
          </p>
        </div>
        <div className="data-sites-access-section">
          {Bixby.Utils.isNullOrEmpty(dataSites) && <NoDataFound dataType="data sites" />}

          {!Bixby.Utils.isNullOrEmpty(dataSites) && (
            <div className="data-sites-access-data-grid">
              <div className="grid_container">
                <div className="grid ag-theme-alpine">
                  <AgGridReact
                    enableRangeSelection={false}
                    getContextMenuItems={getContextMenuItems}
                    defaultColDef={{
                      minWidth: 100,
                      sortable: true,
                      resizable: true,
                      lockPinned: true,
                    }}
                    onGridReady={onGridReady}
                    rowData={dataSites}
                    suppressDragLeaveHidesColumns={true}
                    suppressMovableColumns={true}
                    suppressCellFocus={true}
                    animateRows={true}
                    rowHeight={32}
                    headerHeight={32}
                    components={{
                      mailToCellRenderer: MailToCellRenderer,
                    }}
                    columnDefs={[
                      {
                        field: 'dataSiteName',
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                        headerName: 'Data Site Name',
                        minWidth: 250,
                        pinned: 'left',
                        flex: 1,
                      },
                      {
                        field: 'companyName',
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                        headerName: 'Bixby Access (Company)',
                        cellRenderer: (params: any) => {
                          if (params.data['companyId']) {
                            const url = '/company/' + params.data['companyId'];
                            return (
                              <a className="bix-link link_underline" href={url}>
                                {' '}
                                {params.data['companyName']}{' '}
                              </a>
                            );
                          } else {
                            return <div title={params.data['companyName']}> {params.data['companyName']}</div>;
                          }
                        },
                        minWidth: 250,
                        flex: 1,
                      },
                      {
                        headerName: 'Loan Ticker',
                        field: 'openFigiTicker',
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                        width: 130,
                      },
                      {
                        field: 'dataSiteEmail',
                        filter: 'agTextColumnFilter',
                        headerName: 'Data Site Email',
                        minWidth: 200,
                      },
                      {
                        field: 'declaration',
                        filter: 'agTextColumnFilter',
                        headerName: 'Declaration',
                        minWidth: 150,
                        valueFormatter: (params: any) => {
                          return params.data['declaration'];
                        },
                      },
                      {
                        field: 'notes',
                        filter: 'agTextColumnFilter',
                        floatingFilter: true,
                        headerName: 'Status',
                        minWidth: 250,
                        flex: 1,
                      },
                    ]}></AgGridReact>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
      <Modal title="Data Site Status Legend" active={modalActive} hideClose={true}>
        <DataSitesLegend onCloseModal={handleCloseModal} />
      </Modal>
    </React.Fragment>
  ) : null;

  return <>{content}</>;
};

const mapStateToProps = (state: AppState) => {
  const { isClient } = getIsCurrentUserClientState(state);
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);

  return {
    isAdmin,
    isClient,
    isCreditSightsOpsSufOrRatings: isCreditSightsAnalyst || isCreditSightsOps || isSuFAnalyst || isRatingsAnalyst,
  };
};

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(MnpiDataSitesAccess);
